import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, AlertTriangle, Eye, TrendingUp } from 'lucide-react';
import planchetteIntelService from '../services/planchetteIntelService';

const PlanchetteIntelWidget = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, findingsRes] = await Promise.all([
        planchetteIntelService.getStats().catch(() => ({ data: {} })),
        planchetteIntelService.getFindings({ severity: 'urgent,critical', reviewed: false, limit: 5 }).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setFindings(Array.isArray(findingsRes.data) ? findingsRes.data : (findingsRes.data?.items || []));
    } catch (err) {
      console.error('Failed to load planchette intel widget data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-cyan-500/20 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Search size={18} className="text-cyan-400" />
          <h3 className="text-white font-semibold">PlanchetteIntel</h3>
        </div>
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-400"></div>
        </div>
      </div>
    );
  }

  const totalEntities = stats?.total_entities || 0;
  const urgentCount = findings.length;

  return (
    <div
      className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-cyan-500/20 p-5 cursor-pointer hover:border-cyan-400/40 transition-all"
      onClick={() => navigate('/planchette-intel')}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-lg flex items-center justify-center">
            <Search size={16} className="text-white" />
          </div>
          <h3 className="text-white font-semibold">PlanchetteIntel</h3>
        </div>
        {urgentCount > 0 && (
          <span className="bg-red-500/20 text-red-300 text-xs font-medium px-2 py-0.5 rounded-full border border-red-500/30">
            {urgentCount} urgent
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-virgo-navy-800/50 rounded-lg p-2.5">
          <div className="text-lg font-bold text-white">{totalEntities}</div>
          <div className="text-xs text-virgo-teal-400">Entities</div>
        </div>
        <div className="bg-virgo-navy-800/50 rounded-lg p-2.5">
          <div className="text-lg font-bold text-amber-400">{urgentCount}</div>
          <div className="text-xs text-virgo-teal-400">Unreviewed</div>
        </div>
      </div>

      {findings.length > 0 && (
        <div className="space-y-1.5">
          {findings.slice(0, 3).map(f => (
            <div key={f.id} className="flex items-start gap-2 text-xs">
              <AlertTriangle size={12} className={f.severity === 'urgent' ? 'text-red-400 mt-0.5' : 'text-orange-400 mt-0.5'} />
              <span className="text-virgo-teal-300 line-clamp-1">{f.title}</span>
            </div>
          ))}
        </div>
      )}

      {findings.length === 0 && totalEntities > 0 && (
        <div className="text-xs text-virgo-teal-500 text-center py-2">
          No urgent findings
        </div>
      )}

      {totalEntities === 0 && (
        <div className="text-xs text-virgo-teal-500 text-center py-2">
          Click to start investigating
        </div>
      )}
    </div>
  );
};

export default PlanchetteIntelWidget;
